import { useEffect, useRef, useState } from "react";

import { DataExtractor } from "@impero-com/common/utils/dataView/extractor";
import {
    forEach,
    NodeDescriptor,
} from "@impero-com/common/utils/dataView/source";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    QueryResult,
    SimpleAndFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    BlockPresenceTransition,
    CollapseExpandTextButton,
    CollapseTransition,
} from "@impero-com/ui";
import { RadioChangeEvent } from "antd/es";
import RadioGroup from "antd/es/radio/group";
import RadioButton from "antd/es/radio/radioButton";

import {
    CellCoord,
    RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto,
    EntityOverviewListDto,
    SavedBlob,
} from "api/generated/model";
import { TableHeatmap } from "components/tableHeatmap";
import { RiskMapPageStateRaw } from "org/pageState/convert/risks/common";
import { RiskDirectoryView } from "org/risks/types";

import { MapPageProps } from "..";
import {
    ExpandedRiskNode,
    MapNode,
    MetadataInstance,
    NodeWithMD,
} from "../../types";
import { assertResidualAndInherentHaveTheSameScale } from "../../utils";

import * as theme from "./RiskHeatmap.css";
import {
    HeatMapData,
    INHERENT_MODE,
    MapMode,
    RESIDUAL_MODE,
} from "./RiskHeatmapTypes";

interface GetModeReturns {
    xAxis: {
        values: string[];
        caption: string;
    };
    xToEnum: (x: string) => number;
    yAxis: {
        values: string[];
        caption: string;
    };
    yToEnum: (y: string) => number;
}
type NodeTypeExtractors = Record<
    "Process" | "Risk",
    DataExtractor<NodeWithMD, MetadataInstance>[]
>;

export type { HeatMapData } from "./RiskHeatmapTypes";

type GetModeFunction = (
    riskDirectoryView: RiskDirectoryView,
    mapMode: MapMode
) => GetModeReturns;

type BuildNodeTypeToNodeExtractorsFunction = (
    riskDirectory: RiskDirectoryView,
    savedBlobs: SavedBlob[]
) => NodeTypeExtractors;

type BuildMapQueryResultFunction = (
    riskDirectory: RiskDirectoryView,
    rootNote: RiskDirectoryNodeDto,
    filter: SimpleAndFilter | null,
    entityList: EntityOverviewListDto
) => Promise<QueryResult<NodeDescriptor<MapNode>>>;

type BuildFocusQueryResultFunction = (
    riskDirectory: RiskDirectoryView,
    rootNode: RiskDirectoryNodeDto,
    mapFilter: SimpleAndFilter | null,
    coords: CellCoord[] | null,
    entityList: EntityOverviewListDto,
    mapMode: MapMode
) => Promise<QueryResult<NodeDescriptor<MapNode>>>;

interface RiskHeatmapProps {
    getMode: GetModeFunction;
    buildNodeTypeToNodeExtractors: BuildNodeTypeToNodeExtractorsFunction;
    buildMapQueryResult: BuildMapQueryResultFunction;
    buildFocusQueryResult: BuildFocusQueryResultFunction;
    riskDirectoryView: RiskDirectoryView;
    rootNode: RiskDirectoryNodeDto;
    filter: SimpleAndFilter | null;
    focusCells: CellCoord[] | null;
    entityList: EntityOverviewListDto;
    mode: MapPageProps["mode"];
    pageState: RiskMapPageStateRaw;
    onPageStateChange: (pageState: RiskMapPageStateRaw) => void;
    setFocusCells: (cells: CellCoord[] | null) => void;
}
export function RiskHeatMap({
    getMode,
    buildNodeTypeToNodeExtractors,
    buildMapQueryResult,
    buildFocusQueryResult,
    entityList,
    filter,
    focusCells,
    riskDirectoryView,
    rootNode,
    mode,
    pageState,
    onPageStateChange,
    setFocusCells,
}: RiskHeatmapProps) {
    const { mapMode, showHeatmap, toggleHeatmap, onMapModeChange } =
        useHeatmapControls({
            pageState,
            onPageStateChange,
            setFocusCells,
        });

    const { xAxis, yAxis, xToEnum, yToEnum } = getMode(
        riskDirectoryView,
        mapMode
    );

    const onHeatmapClick = (x: number, y: number) => {
        const index: number | undefined = focusCells?.findIndex(
            (cell) => cell.x === x && cell.y === y
        );
        if (focusCells === null || index === undefined || index < 0) {
            // Add coord to focusCells filter
            setFocusCells([...(focusCells ?? []), { x, y }]);
        } else if (focusCells.length === 1) {
            // Remove single coord in focusCells -> disable filter
            setFocusCells(null);
        } else {
            // Remove clicked coord (at index) from focusCells
            setFocusCells([
                ...(focusCells.slice(0, index) ?? []),
                ...(focusCells.slice(index + 1) ?? []),
            ]);
        }
    };

    const heatMapData = useHeatmapData({
        riskDirectoryView,
        rootNode,
        filter,
        focusCells,
        entityList,
        mapMode,
        mode,
        buildMapQueryResult,
        buildFocusQueryResult,
        buildNodeTypeToNodeExtractors,
    });

    return (
        <RiskHeatMapInner
            xAxis={xAxis}
            yAxis={yAxis}
            xToEnum={xToEnum}
            yToEnum={yToEnum}
            heatMapData={heatMapData}
            showHeatmap={showHeatmap}
            toggleHeatmap={toggleHeatmap}
            mapMode={mapMode}
            onMapModeChange={onMapModeChange}
            onHeatmapClick={onHeatmapClick}
        />
    );
}

interface RiskHeatMapInnerProps {
    xAxis: { values: string[]; caption: string };
    yAxis: { values: string[]; caption: string };
    xToEnum: (x: string) => number;
    yToEnum: (y: string) => number;
    heatMapData: [HeatMapData, HeatMapData | null];
    showHeatmap: boolean;
    toggleHeatmap: () => void;
    mapMode: MapMode;
    onMapModeChange: (e: RadioChangeEvent) => void;
    onHeatmapClick: (x: number, y: number) => void;
}

export function RiskHeatMapInner({
    xAxis,
    yAxis,
    xToEnum,
    yToEnum,
    heatMapData,
    showHeatmap,
    toggleHeatmap,
    mapMode,
    onMapModeChange,
    onHeatmapClick,
}: RiskHeatMapInnerProps) {
    const { t } = useTranslation();

    return (
        <>
            <div className={theme.toggleButtonContainerBackground}>
                <CollapseExpandTextButton
                    onClick={toggleHeatmap}
                    collapsed={!showHeatmap}
                    expandLabel={t("RiskMaps_Show")}
                    collapseLabel={t("RiskMaps_Hide")}
                />
                <BlockPresenceTransition isVisible={showHeatmap} disableInitial>
                    <RadioGroup
                        className={theme.inherentResidualSwitch}
                        value={mapMode}
                        onChange={onMapModeChange}
                    >
                        <RadioButton value="inherent">
                            {t("RiskMaps_Edit_Metadata_Inherent")}
                        </RadioButton>
                        <RadioButton value="residual">
                            {t("RiskMaps_Edit_Metadata_Residual")}
                        </RadioButton>
                    </RadioGroup>
                </BlockPresenceTransition>
            </div>
            <div className={theme.riskMapBackground}>
                <CollapseTransition collapsed={!showHeatmap}>
                    <div className={theme.heatmapContainer}>
                        <HeatmapContent
                            heatMapData={heatMapData}
                            xToEnum={xToEnum}
                            yToEnum={yToEnum}
                            onHeatmapClick={onHeatmapClick}
                            xAxis={xAxis}
                            yAxis={yAxis}
                        />
                    </div>
                </CollapseTransition>
            </div>
        </>
    );
}

interface HeatmapContentProps {
    heatMapData: [HeatMapData, HeatMapData | null];
    xToEnum: (x: string) => number;
    yToEnum: (y: string) => number;
    onHeatmapClick: (x: number, y: number) => void;
    xAxis: {
        values: string[];
        caption: string;
    };
    yAxis: {
        values: string[];
        caption: string;
    };
}

function HeatmapContent({
    heatMapData,
    xToEnum,
    yToEnum,
    onHeatmapClick,
    xAxis,
    yAxis,
}: HeatmapContentProps) {
    const getCellData = (
        [d, diffD]: [HeatMapData, HeatMapData | null],
        x: string,
        y: string
    ) => {
        const xEnum = xToEnum(x);
        const yEnum = yToEnum(y);
        const currentValue = d[xEnum]?.[yEnum]?.focus ?? 0;
        const pastValue = diffD?.[xEnum]?.[yEnum]?.focus ?? 0;

        // If we have diff data, show cells where either current or past has values
        if (diffD !== null) {
            if (currentValue > 0 || pastValue > 0) {
                return {
                    value: currentValue,
                    diffValue: currentValue - pastValue,
                };
            }
            return undefined;
        }

        // No diff data - only show cells with current values
        if (currentValue > 0) {
            return {
                value: currentValue,
                diffValue: 0,
            };
        }
        return undefined;
    };

    const getShadowCellData = (
        heatmaps: [HeatMapData, HeatMapData | null],
        x: string,
        y: string
    ) => {
        const xEnum = xToEnum(x);
        const yEnum = yToEnum(y);
        const d = heatmaps[0];
        const diffD = heatmaps[1];

        const currentCell = d[xEnum]?.[yEnum] ?? { all: 0, focus: 0 };
        const pastCell = diffD?.[xEnum]?.[yEnum] ?? { all: 0, focus: 0 };

        const currentShadow = currentCell.all - currentCell.focus;
        const pastShadow = pastCell.all - pastCell.focus;

        // If we have diff data, show cells where either current or past has shadow values
        if (diffD !== null) {
            if (currentShadow > 0 || pastShadow > 0) {
                return {
                    value: currentShadow,
                    diffValue: currentShadow - pastShadow,
                };
            }
            return undefined;
        }

        // No diff data - only show cells with current shadow values
        if (currentShadow > 0) {
            return {
                value: currentShadow,
                diffValue: 0,
            };
        }
        return undefined;
    };

    return (
        <TableHeatmap<[HeatMapData, HeatMapData | null]>
            data={heatMapData}
            getCellData={getCellData}
            getShadowCellData={getShadowCellData}
            onCircleClick={({ heatmapX, heatmapY }) =>
                onHeatmapClick(xToEnum(heatmapX), yToEnum(heatmapY))
            }
            xAxis={xAxis}
            yAxis={yAxis}
        />
    );
}

function useHeatmapControls({
    pageState,
    onPageStateChange,
    setFocusCells,
}: {
    pageState: RiskMapPageStateRaw;
    onPageStateChange: (pageState: RiskMapPageStateRaw) => void;
    setFocusCells: (cells: CellCoord[] | null) => void;
}) {
    const mapMode: typeof RESIDUAL_MODE | typeof INHERENT_MODE =
        pageState?.showResidualHeatmap ? RESIDUAL_MODE : INHERENT_MODE;

    const showHeatmap = !pageState?.hideHeatmap;

    const toggleHeatmap =
        pageState &&
        onPageStateChange &&
        (() =>
            onPageStateChange({
                ...pageState,
                hideHeatmap: !pageState.hideHeatmap,
            }));

    const unwrapAsMapMode = (value: unknown): MapMode => {
        switch (value) {
            case INHERENT_MODE:
                return INHERENT_MODE;
            case RESIDUAL_MODE:
                return RESIDUAL_MODE;
            default:
                throw new Error(
                    `value is expected to be MapMode, got: ${value}`
                );
        }
    };

    const onMapModeChange =
        pageState &&
        onPageStateChange &&
        setFocusCells &&
        ((e: RadioChangeEvent) => {
            const mapMode = unwrapAsMapMode(e.target.value);
            setFocusCells(null);
            onPageStateChange({
                ...pageState,
                showResidualHeatmap: mapMode === RESIDUAL_MODE,
            });
        });

    return {
        mapMode,
        showHeatmap,
        toggleHeatmap,
        onMapModeChange,
    };
}

function useHeatmapData({
    riskDirectoryView,
    rootNode,
    filter,
    focusCells,
    entityList,
    mapMode,
    mode,
    buildMapQueryResult,
    buildFocusQueryResult,
    buildNodeTypeToNodeExtractors,
}: {
    riskDirectoryView: RiskDirectoryView;
    rootNode: RiskDirectoryNodeDto;
    filter: SimpleAndFilter | null;
    focusCells: CellCoord[] | null;
    entityList: EntityOverviewListDto;
    mapMode: MapMode;
    mode: MapPageProps["mode"];
    buildMapQueryResult: BuildMapQueryResultFunction;
    buildFocusQueryResult: BuildFocusQueryResultFunction;
    buildNodeTypeToNodeExtractors: BuildNodeTypeToNodeExtractorsFunction;
}): [HeatMapData, HeatMapData | null] {
    const buildersRef = useRef({
        buildMapQueryResult,
        buildFocusQueryResult,
        buildNodeTypeToNodeExtractors,
    });
    // eslint-disable-next-line react-hooks/refs
    buildersRef.current = {
        buildMapQueryResult,
        buildFocusQueryResult,
        buildNodeTypeToNodeExtractors,
    };

    const [heatMapData, setHeatMapData] = useState<
        [HeatMapData, HeatMapData | null]
    >([{}, null]);

    useEffect(() => {
        let cancelled = false;

        const getOptionIndex = (
            node: ExpandedRiskNode,
            extractor: DataExtractor<NodeWithMD, MetadataInstance>
        ): number | undefined => {
            const instance = extractor.getValue(node);
            if (instance === undefined) {
                throw new Error("Instance should be present");
            }
            if (instance.type === "MetadataError") {
                return undefined;
            }
            if (instance.type !== "DropdownInstance") {
                throw new Error("Impact/likelihood fields should be dropdowns");
            }
            if (instance.value === undefined) {
                return undefined;
            }
            const optionId = instance.value.id;
            const optionIndex = instance.fieldType.options.findIndex(
                (opt) => opt.id === optionId
            );
            if (optionIndex === -1) {
                throw new Error(
                    `Impact/likelihood option ${optionId} not found`
                );
            }
            return optionIndex;
        };

        const buildHeatMapData = async (
            riskDirectory: RiskDirectoryView,
            rootNode: RiskDirectoryNodeDto,
            filter: SimpleAndFilter | null,
            coords: CellCoord[] | null,
            entityList: EntityOverviewListDto,
            mapMode: MapMode
        ): Promise<HeatMapData> => {
            const {
                buildMapQueryResult,
                buildFocusQueryResult,
                buildNodeTypeToNodeExtractors,
            } = buildersRef.current;

            const [queryResult, focusQueryResult] = await Promise.all([
                buildMapQueryResult(
                    riskDirectory,
                    rootNode,
                    filter,
                    entityList
                ),
                buildFocusQueryResult(
                    riskDirectory,
                    rootNode,
                    filter,
                    coords,
                    entityList,
                    mapMode
                ),
            ]);

            const focusDataSource = focusQueryResult.dataView.dataSource;
            const dataSource = queryResult.dataView.dataSource;

            const data: HeatMapData = {};

            assertResidualAndInherentHaveTheSameScale(riskDirectory);
            const {
                content: {
                    resource: {
                        structure: { blessedFieldIds },
                    },
                },
            } = riskDirectory;

            const extractors = buildNodeTypeToNodeExtractors(
                riskDirectory,
                rootNode.linkedResources.blobs
            )["Risk"];
            const inherentImpactExtractor = extractors.find(
                (ext) => ext.id === blessedFieldIds.inherentImpactId
            )!;
            const inherentLikelihoodExtractor = extractors.find(
                (ext) => ext.id === blessedFieldIds.inherentLikelihoodId
            )!;
            const residualImpactExtractor = extractors.find(
                (ext) => ext.id === blessedFieldIds.residualImpactId
            )!;
            const residualLikelihoodExtractor = extractors.find(
                (ext) => ext.id === blessedFieldIds.residualLikelihoodId
            )!;

            const insertRisk = (
                node: ExpandedRiskNode,
                source: "all" | "focus"
            ) => {
                let impact: number | undefined;
                let likelihood: number | undefined;
                switch (mapMode) {
                    case "inherent":
                        impact = getOptionIndex(node, inherentImpactExtractor);
                        likelihood = getOptionIndex(
                            node,
                            inherentLikelihoodExtractor
                        );
                        break;
                    case "residual":
                        impact = getOptionIndex(node, residualImpactExtractor);
                        likelihood = getOptionIndex(
                            node,
                            residualLikelihoodExtractor
                        );
                        break;
                    default:
                        assertNever(mapMode);
                }
                if (impact !== undefined && likelihood !== undefined) {
                    if (data[likelihood] === undefined) {
                        data[likelihood] = {};
                    }
                    if (data[likelihood][impact] === undefined) {
                        data[likelihood][impact] = { all: 0, focus: 0 };
                    }
                    data[likelihood][impact][source]++;
                }
            };

            forEach(focusDataSource.iter(), (desc) => {
                if (desc.value.type === "ExpandedRiskNode") {
                    insertRisk(desc.value, "focus");
                }
            });

            forEach(dataSource.iter(), (desc) => {
                if (desc.value.type === "ExpandedRiskNode") {
                    insertRisk(desc.value, "all");
                }
            });

            return data;
        };

        const currentPromise = buildHeatMapData(
            riskDirectoryView,
            rootNode,
            filter,
            focusCells,
            entityList,
            mapMode
        );
        const diffPromise =
            mode.type === "InsightsMap"
                ? buildHeatMapData(
                      mode.diffData.riskDirData!,
                      mode.diffData.riskNodesData!,
                      filter,
                      focusCells,
                      mode.diffData.riskEntitiesData!,
                      mapMode
                  )
                : Promise.resolve(null);

        Promise.all([currentPromise, diffPromise]).then(([current, diff]) => {
            if (!cancelled) {
                setHeatMapData([current, diff]);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [
        riskDirectoryView,
        rootNode,
        filter,
        focusCells,
        entityList,
        mapMode,
        mode,
    ]);

    return heatMapData;
}
